import React, { useEffect, useState } from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import UserDetails from "./UserDetails";
import { isRemovingLastOwner } from "./lastOwnerGuard";
import { OrgUserRole, Profile, ProjectUserRole } from "../common/ObjectTypes";
import { getUserDetails, updateUser, inviteUser } from "../../api/usersApi";
import { toast } from "react-toastify";
import { useTranslation } from "react-i18next";
import { useProfile } from "../common/ProfileProvider";
import {
  getOrganization,
  getOrganizationUsers,
  removeUserFromOrganization,
} from "../../api/organizationsApi";
import {
  getProject,
  getProjectUsers,
  removeUserFromProject,
} from "../../api/projectsApi";
import { OrganizationRoles, ProjectRoles } from "../common/Enums";

type ManageUserProps = {
  existingUser: boolean;
  newAccount: boolean;
};

const ManageUserDetails = ({ existingUser, newAccount }: ManageUserProps) => {
  const { t } = useTranslation();
  const { user_id } = useParams();
  // check to see if current url ends with /myaccount
  const location = useLocation();
  const isMyAccount: boolean = location.pathname.endsWith("/myaccount");
  const navigate = useNavigate();
  const { profile, profileLoading, fetchProfile } = useProfile();
  const [permissionsLoaded, setPermissionsLoaded] = useState<boolean>(false);
  const [errors, setErrors] = useState({});
  const [userLoading, setUserLoading] = useState<boolean>(true);
  const [roleLoading, setRoleLoading] = useState<boolean>(false);
  const [isSaving, setIsSaving] = useState<boolean>(false);
  const emptyUser: Profile = {
    user_id: "",
    first_name: "",
    last_name: "",
    email: "",
    organization: "",
    job_title: "",
    is_admin: false,
    is_verified: false,
    is_active: false,
    org_roles: [],
    project_roles: [],
    theme: null,
  };
  const [user, setUser] = useState<Profile>(emptyUser);
  const [unchangedUser, setUnchangedUser] = useState<Profile>(emptyUser);

  useEffect(() => {
    if (!profileLoading) {
      setPermissionsLoaded(true);
    }
  }, [profileLoading, profile]);

  useEffect(() => {
    const loadUser = async () => {
      if (isMyAccount && profile) {
        setUser(profile);
        setUnchangedUser(profile); // unchanged user object is used to revert changes back to what is saved in the db if the cancel button is clicked
      }
      if (!isMyAccount && user_id) {
        const tempUser = await getUserDetails(user_id);
        setUser(tempUser);
        setUnchangedUser(tempUser);
      }
      setUserLoading(false);
    };

    if (permissionsLoaded && (user_id || isMyAccount) && existingUser)
      loadUser();
  }, [permissionsLoaded, user_id, roleLoading, profile]);

  const handleChange = (e: any) => {
    setUser({
      ...user,
      [e.target.id]:
        e.target.type === "checkbox" ? e.target.checked : e.target.value,
    });
  };

  const formIsValid = (): boolean => {
    const newErrors: any = {};

    if (!user.first_name) {
      newErrors.first_name = t("First name is required");
    }
    if (user.first_name && user.first_name.length > 255) {
      newErrors.first_name = t("Name length exceeds 255 character limit");
    }
    if (!user.last_name) {
      newErrors.last_name = t("Last name is required");
    }
    if (user.last_name && user.last_name.length > 255) {
      newErrors.last_name = t("Name length exceeds 255 character limit");
    }
    if (!user.email) {
      newErrors.email = t("Email is required");
    }
    if (user.email && user.email.length > 255) {
      newErrors.email = t("Email length exceeds 255 character limit");
    }
    if (user.organization && user.organization.length > 255) {
      newErrors.organization = t(
        "Organization length exceeds 255 character limit",
      );
    }
    if (user.job_title && user.job_title.length > 255) {
      newErrors.job_title = t("Job title length exceeds 255 character limit");
    }

    setErrors(newErrors);

    return Object.keys(newErrors).length === 0;
  };

  const handleSave = async () => {
    const userDto: any = {
      first_name: user.first_name,
      last_name: user.last_name,
      email: user.email.toLocaleLowerCase(),
      organization: user.organization,
      job_title: user.job_title,
      is_admin: user.is_admin,
      is_active: user.is_active,
    };

    setIsSaving(true);

    let canSave = formIsValid();
    if (!canSave) {
      setIsSaving(false);
      return;
    }

    try {
      if (user_id || isMyAccount) {
        await updateUser(user.user_id, userDto);
        toast.success(t("User updated"));
        if (newAccount) {
          navigate("/");
        } else {
          navigate(-1);
        }
      } else {
        await inviteUser(userDto);
        toast.success(t("User invited"));
        navigate("/users/");
      }
    } catch (error: any) {
      if (user_id || isMyAccount) {
        toast.error(t("Error updating user: ") + error.data.detail.toString());
      } else {
        toast.error(t("Error sending invite: ") + error.data.detail.toString());
      }
    } finally {
      setIsSaving(false);
    }
  };

  const handleRemoveOrgRole = async (role: OrgUserRole) => {
    setRoleLoading(true);
    try {
      // Only an Owner can be the last Owner, and only Owners and Editors may list
      // members (AKL-843), so every other role skips the fetch and simply leaves.
      const isLastOwner = await isRemovingLastOwner(
        role.role,
        OrganizationRoles.OWNER,
        () => getOrganizationUsers({ org_id: Number(role.org_id) }),
      );
      if (isLastOwner) {
        toast.error(
          `${user.first_name} ${user.last_name} ${t(
            "is the only owner of that organization.",
          )} ${t("Please assign another owner before removing this user.")}`,
        );
      } else {
        await removeUserFromOrganization(role.org_id, user.user_id);
        if (isMyAccount) {
          // reload the user's data after user role was removed
          await fetchProfile();
        }
        toast.success(t("User Removed"));
      }
    } catch (err) {
      toast.error(t("Error Removing Invitation"));
    }
    setRoleLoading(false);
  };

  const handleRemoveProjectRole = async (role: ProjectUserRole) => {
    setRoleLoading(true);
    try {
      // Same rule as the organization path: fetch only when leaving as Owner.
      const isLastOwner = await isRemovingLastOwner(
        role.role,
        ProjectRoles.OWNER,
        () => getProjectUsers({ proj_id: role.project_id }),
      );
      if (isLastOwner) {
        toast.error(
          `${user.first_name} ${user.last_name} ${t(
            "is the only owner of that project.",
          )} ${t("Please assign another owner before removing this user.")}`,
        );
      } else {
        await removeUserFromProject(role.project_id, user.user_id);
        if (isMyAccount) {
          // reload the user's data after user role was removed
          await fetchProfile();
        }
        toast.success(t("User Removed"));
      }
    } catch (err) {
      toast.error(t("Error Removing Invitation"));
    }
    setRoleLoading(false);
  };

  const handleCancelButton = () => {
    if (existingUser) setUser(unchangedUser);
    navigate(-1);
  };

  /**
   * Navigates to the project overview page for the selected project.
   * @param project_id the ID of the project to navigate to
   * @throws Error if the project is not found
   */
  const handleSelectProject = async (project_id: number) => {
    setUserLoading(true);
    const project = await getProject(project_id);
    if (!project || !project?.guid) {
      throw new Error("Project not found");
    }
    navigate("/projects/" + (project.guid as string) + "/overview");
    setUserLoading(false);
  };

  /**
   * Navigates to the organization overview page for the selected organization.
   * @param org_id the ID of the organization to navigate to
   * @throws Error if the organization is not found
   */
  const handleSelectOrganization = async (org_id: number) => {
    setUserLoading(true);
    const org = await getOrganization(org_id);
    if (!org || !org?.guid) {
      throw new Error("Organization not found");
    }
    navigate("/organizations/" + (org.guid as string) + "/overview");
    setUserLoading(false);
  };

  return existingUser && userLoading ? (
    <div>{t("Loading...")}</div>
  ) : (
    <div>
      <UserDetails
        user={user}
        existingUser={existingUser}
        roleLoading={roleLoading}
        isMyAccount={isMyAccount}
        onChange={handleChange}
        onCancel={handleCancelButton}
        onSave={handleSave}
        onRemoveOrgRole={handleRemoveOrgRole}
        onRemoveProjectRole={handleRemoveProjectRole}
        errors={errors}
        newAccount={newAccount}
        profile={profile}
        isSaving={isSaving}
        saveDisabled={JSON.stringify(unchangedUser) === JSON.stringify(user)}
        onSelectProject={handleSelectProject}
        onSelectOrganization={handleSelectOrganization}
      />
    </div>
  );
};

export default ManageUserDetails;
